import { useState } from "react";
import { X } from "lucide-react";
import { Button, Label } from "@/design-system/components/ui";
import {
  brokeragesApi,
  type BrokerageDetailDto,
  type BrokerageStatus,
} from "@/api/brokerages";

/**
 * Brokerage status control — the off-ramp / reactivate / delete companion to
 * the pipeline stepper. Forward pipeline moves (Applied → Under review →
 * Certified) live on the stepper; this modal handles taking a firm off the
 * pipeline (Inactive / Suspended / Declined / Archived), reactivating an
 * off-pipeline firm back to Prospect, and deletion. Non-archive transitions
 * route through the manual status endpoint (IBrokerageWorkflowService);
 * Archived keeps the dedicated archive endpoint (preserving its audit action).
 */
// The management transitions this modal offers (the stepper owns forward
// pipeline advancement). Prospect is included so an archived/off-ramp firm —
// where the stepper is inert — can re-enter the funnel.
const MODAL_TARGETS: BrokerageStatus[] = [
  "Prospect", "Inactive", "Suspended", "Declined", "Archived",
];
// "Deleted" is not a stored status — it routes through the soft-delete
// endpoint — but it is surfaced here as a status target so the destructive
// action lives alongside the other transitions. Only offered to users with
// Brokerages.Delete.
const DELETED = "Deleted" as const;
type StatusTarget = BrokerageStatus | typeof DELETED | "";

export function BrokerageStatusModal({
  brokerage,
  canDelete = false,
  onClose,
  onChanged,
  onDeleted,
}: {
  brokerage: BrokerageDetailDto;
  canDelete?: boolean;
  onClose: () => void;
  onChanged: () => void;
  onDeleted?: () => void;
}) {
  const [target, setTarget] = useState<StatusTarget>("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const targets: StatusTarget[] = [
    ...MODAL_TARGETS.filter((s) => s !== brokerage.status),
    ...(canDelete ? [DELETED] : []),
  ];

  const submit = async () => {
    if (target === "") return;
    setError(null);
    setSaving(true);
    try {
      if (target === DELETED) {
        await brokeragesApi.delete(brokerage.id);
        (onDeleted ?? onChanged)();
        onClose();
        return;
      }
      if (target === "Archived") {
        await brokeragesApi.archive(brokerage.id);
      } else {
        // Manual transition (IBrokerageWorkflowService) — no prerequisites,
        // no certification gate.
        await brokeragesApi.changeStatus(brokerage.id, target);
      }
      onChanged();
      onClose();
    } catch (e) {
      setError((e as Error).message ?? "Status change failed.");
      setSaving(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Change status — ${brokerage.name}`}
      className="fixed inset-0 z-40 bg-onyx/50 flex items-center justify-center p-space-4"
    >
      <div
        className="bg-white-pure rounded-lg shadow-lg max-w-lg w-full p-space-5 border-t-[2px] border-t-gold"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-space-3">
          <div>
            <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-1">
              Brokerage
            </div>
            <h2 className="font-display font-bold text-h3 text-onyx m-0">Change status</h2>
            <div className="text-caption text-gray-mid mt-space-1">
              {brokerage.name} — currently {brokerage.status}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-gray-mid hover:text-onyx">
            <X size={18} strokeWidth={1.5} aria-hidden />
          </button>
        </div>

        <div className="grid gap-space-3">
          <div>
            <Label className="mb-space-1 block">Set status to</Label>
            <div className="grid grid-cols-2 gap-space-2">
              {targets.map((s) => (
                <ActionRadio
                  key={s}
                  label={s}
                  active={target === s}
                  destructive={s === DELETED}
                  onClick={() => setTarget(s)}
                />
              ))}
            </div>
          </div>

          {error && <div className="text-small text-status-error">{error}</div>}

          <div className="flex items-center justify-end gap-space-2">
            <Button variant="text" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button
              size="sm"
              variant={target === DELETED ? "destructive" : "primary"}
              onClick={submit}
              disabled={saving || target === ""}
            >
              {saving ? "Saving…" : target === DELETED ? "Delete" : "Apply"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ActionRadio({
  label, active, destructive = false, onClick,
}: { label: string; active: boolean; destructive?: boolean; onClick: () => void }) {
  const base = "rounded-md border px-space-3 py-space-2 text-small text-left transition-colors";
  const cls = active
    ? destructive
      ? "border-status-error bg-status-error/10 text-status-error font-semibold"
      : "border-gold bg-gold/10 text-onyx font-semibold"
    : destructive
      ? "border-border-light bg-white-pure text-status-error hover:border-status-error"
      : "border-border-light bg-white-pure text-gray-mid hover:border-gold-shadow";
  return (
    <button type="button" onClick={onClick} className={`${base} ${cls}`}>
      {label}
    </button>
  );
}
